import { DatePipe, NgOptimizedImage } from '@angular/common'
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output,
    signal,
} from '@angular/core'
import type {
    BookingSlotAreaGroup,
    BookingSlotChoice,
} from '../../../domain/Availability'

@Component({
    selector: 'app-venue-storefront-booking-area-slots',
    standalone: true,
    imports: [
        DatePipe,
        NgOptimizedImage,
    ],
    templateUrl: './venue-storefront-booking-area-slots.component.html',
    styleUrls: ['./venue-storefront-booking-area-slots.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueStorefrontBookingAreaSlotsComponent {
    group = input.required<BookingSlotAreaGroup>()
    idealTime = input.required<Date>()
    cardId = input.required<string>()
    collapsible = input(true)
    selectedSlot = input<BookingSlotChoice | null>(null)
    slotSelected = output<BookingSlotChoice>()
    waitlistSlotSelected = output<BookingSlotChoice>()
    joinWaitlistRequested = output<void>()

    protected readonly expanded = signal(true)
    private readonly selectedPage = signal<number | null>(null)
    private readonly pageSize = 12
    private readonly availableSlotCount = computed(() => {
        return this.group().slots.filter(slot => this.isReservable(slot)).length
    })
    protected readonly hasAvailability = computed(() => this.availableSlotCount() > 0)
    protected readonly hasWaitlistSlots = computed(() => {
        return this.group().slots.some(slot => this.isWaitlistOnly(slot))
    })
    protected readonly hasSelectableSlots = computed(() => {
        return this.hasAvailability() || this.hasWaitlistSlots()
    })
    protected readonly canCollapse = computed(() => {
        return this.collapsible() && this.hasSelectableSlots()
    })
    protected readonly isExpanded = computed(() => !this.canCollapse() || this.expanded())
    protected readonly totalPages = computed(() => {
        return Math.ceil(this.group().slots.length / this.pageSize)
    })
    protected readonly currentPage = computed(() => {
        const lastPage = Math.max(this.totalPages() - 1, 0)
        return Math.min(this.selectedPage() ?? this.closestPage(), lastPage)
    })
    protected readonly visibleSlots = computed(() => {
        const start = this.currentPage() * this.pageSize
        return this.group().slots.slice(start, start + this.pageSize)
    })

    protected toggle() {
        if (this.canCollapse()) {
            this.expanded.update(expanded => !expanded)
        }
    }

    protected previousPage() {
        this.selectedPage.set(Math.max(this.currentPage() - 1, 0))
    }

    protected nextPage() {
        this.selectedPage.set(Math.min(this.currentPage() + 1, this.totalPages() - 1))
    }

    protected selectSlot(slot: BookingSlotChoice) {
        if (this.isReservable(slot)) {
            this.slotSelected.emit(slot)
        } else if (this.isWaitlistOnly(slot)) {
            this.waitlistSlotSelected.emit(slot)
        }
    }

    protected isReservable(slot: BookingSlotChoice): boolean {
        return slot.reservableArea !== null && slot.reservableOption !== null
    }

    protected isWaitlistOnly(slot: BookingSlotChoice): boolean {
        return !this.isReservable(slot) && slot.waitlistArea !== null
    }

    protected isSelectable(slot: BookingSlotChoice): boolean {
        return this.isReservable(slot) || this.isWaitlistOnly(slot)
    }

    protected isSelected(slot: BookingSlotChoice): boolean {
        const selectedSlot = this.selectedSlot()
        if (selectedSlot === null) {
            return false
        }
        return selectedSlot.bookingSlot.equals(slot.bookingSlot)
            && selectedSlot.reservableArea?.id === slot.reservableArea?.id
    }

    private closestPage(): number {
        const slots = this.group().slots
        if (slots.length === 0) {
            return 0
        }
        const closestIndex = slots.reduce((closest, slot, index) => {
            const difference = Math.abs(
                slot.bookingSlot.dateTime.getTime() - this.idealTime().getTime()
            )
            const closestDifference = Math.abs(
                slots[closest].bookingSlot.dateTime.getTime() - this.idealTime().getTime()
            )
            return difference < closestDifference ? index : closest
        }, 0)
        const closestPage = Math.floor(closestIndex / this.pageSize)
        if (this.pageHasSelectableSlot(closestPage)) {
            return closestPage
        }
        for (let distance = 1; distance < this.totalPages(); distance += 1) {
            const earlierPage = closestPage - distance
            if (earlierPage >= 0 && this.pageHasSelectableSlot(earlierPage)) {
                return earlierPage
            }
            const laterPage = closestPage + distance
            if (laterPage < this.totalPages() && this.pageHasSelectableSlot(laterPage)) {
                return laterPage
            }
        }
        return closestPage
    }

    private pageHasSelectableSlot(page: number): boolean {
        const start = page * this.pageSize
        return this.group().slots
            .slice(start, start + this.pageSize)
            .some(slot => this.isSelectable(slot))
    }
}
